import i18n from '@/plugins/vue-i18n';
import type { PaymentRequest, PaymentRequestBulkOrder } from '@/types/api/organisations.types';

/**
What a bulk refresh reads off a request: the bulk order it settles.
*/
export type BulkOrderMarked = { bulkOrder: PaymentRequestBulkOrder };

export type BulkPaymentRequest = PaymentRequest & BulkOrderMarked;

/**
 * Whether something settles a bulk order: the `bulkOrder` marker, and nothing else. Anything
 * carrying the marker answers, such as the request Log payment holds selected, which keeps the
 * marker and little more of the request.
 *
 * Context: never inferred from `order` or `siteId` being empty, because an advance (ADV) request
 * has neither and is no bulk request.
 */
export const isBulkOrderMarked = <T extends { bulkOrder?: PaymentRequestBulkOrder | null }>(
  carrier: T | null | undefined,
): carrier is T & BulkOrderMarked => !!carrier?.bulkOrder;

/**
 * Whether a payment request settles a bulk order, by the marker alone. Every bulk variant of a
 * payment request screen, the decline confirm first, chooses itself through this one answer, so an
 * ordinary request, which finance handles daily, keeps the screens it has always had.
 */
export const isBulkPaymentRequest = (
  paymentRequest: PaymentRequest | null | undefined,
): paymentRequest is BulkPaymentRequest => isBulkOrderMarked(paymentRequest);

/**
The decline confirm's button label key, which on a bulk request names the bulk order it cancels.
*/
export const declineConfirmLabelKey = (
  paymentRequest: PaymentRequest | null | undefined,
): string =>
  isBulkPaymentRequest(paymentRequest)
    ? 'accounts.paymentRequests.bulkDecline.confirm'
    : 'accounts.paymentRequests.details.declineModal.confirm';

/**
The distinct sites a bulk request's linked orders sit on; a linked order may carry none.
*/
export const linkedSiteIds = (paymentRequest: PaymentRequest): string[] => [
  ...new Set(
    (paymentRequest.linkedOrders ?? [])
      .map((linkedOrder) => linkedOrder.siteId)
      .filter((siteId): siteId is string => !!siteId),
  ),
];

/**
 * "3 sites": how a bulk request names its sites wherever a normal one names its site. A count of
 * the served linked orders' sites, so the lists, the drawer and the emails agree.
 */
export const bulkSiteCountLabel = (paymentRequest: PaymentRequest): string => {
  const count = linkedSiteIds(paymentRequest).length;
  return i18n.global.t('accounts.paymentRequests.bulkSiteCount', { count }, count);
};
